/**
 * BillingCycleToggle — monthly or annual, the ONE way a visitor chooses (APP-ENTITLEMENT-001 Phase 11): the
 * pricing section on every site and the signup basket render this toggle, over page-shell's `<TogglePillGroup>`
 * (the ONE interactive segmented pill), with annual named by what it gives away — "two months free" (Q-AE-26:
 * annual is exactly ten months' price for twelve).
 *
 * @module @puffin-core/marketing-shell/pricing/BillingCycleToggle
 */

import PropTypes from 'prop-types';
import { BILLING_CYCLE } from '@puffin-core/app-catalog';
// The SUBPATH, never the page-shell barrel (PERF-FECSS-002): this toggle renders on nine prerendered sites.
import { TogglePillGroup } from '@puffin-core/page-shell/toggle-pill-group';

import { useMarketingShellTranslate } from '../hooks/useMarketingShellTranslate.js';
import { ANNUAL_FREE_MONTHS } from './pricingRules.js';

/**
 * A billing cycle: one of `@puffin-core/app-catalog`'s `BILLING_CYCLE` values, read off the frozen object itself.
 *
 * @typedef {typeof BILLING_CYCLE[keyof typeof BILLING_CYCLE]} BillingCycle
 */

/**
 * @param {Object} props
 * @param {string} props.billingCycle - `monthly` | `annual`.
 * @param {(cycle: BillingCycle) => void} props.onCycleChange - called with the cycle chosen; the toggle emits only
 *   `BILLING_CYCLE` values.
 * @param {Function} [props.t] - the host's translator, honoured first.
 * @param {string} [props.testId='billing-cycle']
 * @param {'sm'|'md'} [props.size='md']
 * @returns {React.JSX.Element}
 */
export function BillingCycleToggle({
  billingCycle,
  onCycleChange,
  t: hostT,
  testId = 'billing-cycle',
  size = 'md',
}) {
  const t = useMarketingShellTranslate(hostT);
  const isAnnual = billingCycle === BILLING_CYCLE.ANNUAL;
  return (
    <TogglePillGroup
      ariaLabel={t('pricing.cycleAria')}
      testId={testId}
      size={size}
      options={[
        {
          value: BILLING_CYCLE.MONTHLY,
          label: t('pricing.cycleMonthly'),
          isActive: !isAnnual,
          onSelect: () => onCycleChange(BILLING_CYCLE.MONTHLY),
        },
        {
          value: BILLING_CYCLE.ANNUAL,
          label: t('pricing.cycleAnnual', { free: String(ANNUAL_FREE_MONTHS) }),
          isActive: isAnnual,
          onSelect: () => onCycleChange(BILLING_CYCLE.ANNUAL),
        },
      ]}
    />
  );
}

BillingCycleToggle.propTypes = {
  billingCycle: PropTypes.oneOf([BILLING_CYCLE.MONTHLY, BILLING_CYCLE.ANNUAL]).isRequired,
  onCycleChange: PropTypes.func.isRequired,
  t: PropTypes.func,
  testId: PropTypes.string,
  size: PropTypes.oneOf(['sm', 'md']),
};

export default BillingCycleToggle;
